import {
  call,
  put,
  takeEvery,
  select,
  delay,
  takeLeading,
  all,
} from 'redux-saga/effects'
import {
  types,
  IUploadFileAction,
  ICreateOrUpdateEditionAction,
  IReadLocalFileAction,
  IReadLocalFileForPreviewAction,
  IUploadPreviewAction,
  IDeleteUploadedPreviewsAction,
  IPreviewPollingSuccessAction,
  IVerificationPollingFailureAction,
  ICreateOrUpdateEditionPollingSuccess,
  IVerificationPollingSuccessAction,
  ICreateTmpLinkResponse,
} from '../types/file'
import { PackType, IEditionMetadata } from '../types/pack'
import { ICapabilitiesSummary } from '../types/capabilitiesSummary'
import { push } from '../actions/router'
import * as actions from '../actions/file'
import * as packActions from '../actions/pack'
import * as pollingActions from '../actions/polling'
import * as selectors from '../selectors/file'
import * as packSelectors from '../selectors/pack'
import { selectCapabilitiesSummaryPerGroup } from '../selectors/capabilitiesSummary'
import * as services from '../services/file'
import * as packServices from '../services/pack'
import { trace } from '../services/common'
import { messages } from '../config/messages'

export function* uploadSaga(action: IUploadFileAction) {
  try {
    const extension: string = yield select(selectors.selectFileExtension)
    const existingEdition: IEditionMetadata | undefined = yield select(
      selectors.selectExistingEdition
    )

    const fileName = existingEdition
      ? existingEdition.file.fileName
      : `${action.name}.${extension}`

    const editions: IEditionMetadata[] = yield select(
      packSelectors.selectEditions
    )
    const matchedEdition = packServices.getMatchedEdition(editions, fileName)
    if (!matchedEdition || existingEdition) {
      const { uploadURI, id }: ICreateTmpLinkResponse = yield call(
        services.createTmpLink,
        fileName
      )
      yield put(actions.retrievedTmpLink(id))
      const fileInstance: File = yield select(selectors.selectFileInstance)
      yield call(
        services.uploadWithProgress,
        uploadURI,
        fileInstance,
        action.onProgress
      )
      yield put(actions.uploadSuccess())
      const fileId: string = yield select(selectors.selectFileId)
      yield delay(1000)
      yield put(
        pollingActions.dataPollingAction(
          `${services.fileUrl}/${fileId}`,
          Date.now(),
          actions.verificationPollingSuccess(action.groupId),
          actions.verificationPollingFailure
        )
      )
    } else {
      yield put(actions.uploadCancel(matchedEdition))
      // Reopen the UploadModal with some delay...
      // ...so React won't batch these two actions together and therefore the form gets reinitialized
      yield delay(200)
      yield put(actions.reopenUploadModal(action.modalId))
    }
  } catch (err) {
    trace(err)
    yield put(actions.uploadFailure(err.message, err.correlationId))
  }
}

export function* verificationPollingSuccessSaga(
  action: IVerificationPollingSuccessAction
) {
  const pack: PackType = yield select(packSelectors.selectPack)
  const fileId: string = yield select(selectors.selectFileId)
  yield delay(1000) // Delaying for visual effect
  yield put(actions.verificationSuccess())
  if (!!pack.id) {
    yield put(actions.createOrUpdateEdition(fileId))
  } else {
    yield put(packActions.createPackFromFile(action.groupId, fileId))
  }
}

export function* verificationPollingFailureSaga(
  action: IVerificationPollingFailureAction
) {
  yield put(
    actions.verificationFailure(
      messages.fileVerificationFailure,
      action.correlationId,
      true
    )
  )
}

export function* createOrUpdateEditionSaga(
  action: ICreateOrUpdateEditionAction
) {
  try {
    const pack = yield select(packSelectors.selectPack)
    const existingEdition = yield select(selectors.selectExistingEdition)
    const editions = yield select(selectors.selectEditionsToUpdate)
    const { location } = !!existingEdition
      ? yield call(packServices.updateFileInEdition, pack.id, {
          fileId: action.fileId,
          editions,
          modified: pack.modified,
        })
      : yield call(packServices.createEditionFromFile, pack.id, {
          fileId: action.fileId,
        })
    yield put(
      pollingActions.dataPollingAction(
        location,
        Date.now(),
        actions.createOrUpdateEditionPollingSuccess,
        actions.createOrUpdateEditionFailure
      )
    )
  } catch (err) {
    trace(err)
    yield put(
      actions.createOrUpdateEditionFailure(err.message, err.correlationId)
    )
  }
}

export function* createOrUpdateEditionPollingSuccessSaga(
  action: ICreateOrUpdateEditionPollingSuccess
) {
  try {
    const fileName = yield select(selectors.selectFileName)
    const existingEdition = yield select(selectors.selectExistingEdition)
    const formattedFileName = services.formatFileName(
      !!existingEdition ? existingEdition.file.fileName : fileName
    )
    yield put(
      actions.createOrUpdateEditionSuccess(
        !existingEdition
          ? messages.createEditionForFileCreateSuccess(formattedFileName)
          : messages.createEditionForFileUpdateSuccess(formattedFileName)
      )
    )
    const packId = yield select(packSelectors.selectPackId)
    const groupId = yield select(packSelectors.selectParentGroup)
    // Delaying for visual effect
    yield delay(1000)
    yield put(actions.closeUploadDialog())
    yield put(actions.resetFileData())
    yield put(packActions.getPackById(packId))
    if (!existingEdition) {
      yield put(
        push(
          `/group/${groupId}/pack/${packId}?tab=publish&edition=${action.data.editionId}`
        )
      )
    }
  } catch (err) {
    trace(err)
    yield put(
      actions.createOrUpdateEditionFailure(err.message, err.correlationId)
    )
  }
}

export function* readLocalFileSaga(action: IReadLocalFileAction) {
  const {
    maxFileSizeMb,
    allowedExtensions,
    editionTypes,
  }: ICapabilitiesSummary = yield select(
    selectCapabilitiesSummaryPerGroup(action.groupId)
  )
  const message = services.validateFileInstance(
    action.fileInstance,
    maxFileSizeMb,
    allowedExtensions,
    editionTypes
  )
  if (message?.length > 0) {
    yield put(actions.readLocalFileValidationFailure(message))
  }
}

export function* readLocalFileForPreviewSaga(
  action: IReadLocalFileForPreviewAction
) {
  const extension = yield select(selectors.selectFileExtension)
  if (!['png', 'jpg', 'jpeg'].includes(extension.toLowerCase())) {
    yield put(
      actions.readLocalFileValidationFailure([messages.fileValidationFailure])
    )
  }
}

export function* uploadPreviewSaga(action: IUploadPreviewAction) {
  try {
    const fileInstance: File = yield select(selectors.selectFileInstance)
    const fileExtension: string = yield select(selectors.selectFileExtension)
    const { uploadURI, id }: ICreateTmpLinkResponse = yield call(
      services.createTmpLink,
      `${action.fileName}.${fileExtension}`
    )
    yield put(actions.retrievedTmpLink(id))
    yield call(
      services.uploadWithProgress,
      uploadURI,
      fileInstance,
      action.onProgress
    )
    yield put(actions.uploadSuccess())
    yield put(
      pollingActions.dataPollingAction(
        `${services.fileUrl}/${id}`,
        Date.now(),
        () => actions.previewPollingSuccess(action.setPreviewField),
        actions.verificationFailure
      )
    )
  } catch (err) {
    trace(err)
    yield put(actions.uploadFailure(err.message, err.correlationId))
  }
}

export function* previewPollingSuccessSaga(
  action: IPreviewPollingSuccessAction
) {
  const fileId = yield select(selectors.selectFileId)
  action.setPreviewField(fileId)
  yield put(actions.previewVerificationSuccess())
  yield delay(1000)
  yield put(actions.closeUploadDialog())
}

export function* deleteUploadedPreviewsSaga(
  action: IDeleteUploadedPreviewsAction
) {
  try {
    const newlyUploadedPreviews = [
      ...action.values.editions.map(
        (edition) => packServices.getPreviewImages(edition).newFileId
      ),
      packServices.getPreviewImages(action.values).newFileId,
    ].filter(Boolean) as string[]
    yield all(
      newlyUploadedPreviews.map((id) => call(services.deleteFileById, id))
    )
  } catch (err) {
    trace(err)
    yield put(
      actions.deleteUploadedPreviewsFailure(err.message, err.correlationId)
    )
  }
}

export function* fileWatcher() {
  yield takeLeading(types.UPLOAD__STARTED, uploadSaga)
  yield takeEvery(
    types.CREATE_OR_UPDATE_EDITION__STARTED,
    createOrUpdateEditionSaga
  )
  yield takeEvery(types.READ_LOCAL_FILE, readLocalFileSaga)
  yield takeEvery(
    types.READ_LOCAL_FILE_FOR_PREVIEW,
    readLocalFileForPreviewSaga
  )
  yield takeLeading(types.UPLOAD_PREVIEW__STARTED, uploadPreviewSaga)
  yield takeEvery(types.PREVIEW_POLLING__SUCCESS, previewPollingSuccessSaga)
  yield takeEvery(
    types.DELETE_UPLOADED_PREVIEWS__STARTED,
    deleteUploadedPreviewsSaga
  )
  yield takeLeading(
    types.VERIFICATION_POLLING__SUCCESS,
    verificationPollingSuccessSaga
  )
  yield takeLeading(
    types.VERIFICATION_POLLING__FAILURE,
    verificationPollingFailureSaga
  )
  yield takeEvery(
    types.CREATE_OR_UPDATE_EDITION_POLLING__SUCCESS,
    createOrUpdateEditionPollingSuccessSaga
  )
}
